/**
 * TemplatePickerModal.tsx
 *
 * Modal for selecting and instantiating structured TaskFlow Project & Workflow Templates.
 */

import React, { useState } from 'react';
import {
  TaskFlowTemplateRegistry,
  type TaskFlowTemplate,
} from '../../../plugins/taskflow/contracts/taskflow-templates.js';
import { globalTaskFlowStore } from '../../../plugins/taskflow/engine/taskflow-store.js';

export interface TemplatePickerModalProps {
  isOpen: boolean;
  workspaceId: string;
  onClose: () => void;
  onInstantiated?: () => void;
}

export const TemplatePickerModal: React.FC<TemplatePickerModalProps> = ({
  isOpen,
  workspaceId,
  onClose,
  onInstantiated,
}) => {
  const templates = TaskFlowTemplateRegistry.getAvailableTemplates();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(templates[0]?.id || '');
  const [baseDate, setBaseDate] = useState<string>(new Date().toISOString().split('T')[0]);

  if (!isOpen) return null;

  const handleInstantiate = () => {
    if (!selectedTemplateId) return;

    const items = TaskFlowTemplateRegistry.instantiateTemplate(selectedTemplateId, {
      workspaceId,
      baseDate,
      actorId: 'user',
    });

    for (const item of items) {
      globalTaskFlowStore.createItem({
        workspaceId,
        type: item.type,
        title: item.title,
        description: item.description || undefined,
        status: item.status,
        priority: item.priority as any,
        startDate: item.startDate,
        dueDate: item.dueDate,
        parent: item.parent,
        tags: item.tags,
      }, 'template');
    }

    onInstantiated?.();
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        zIndex: 110,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '620px',
          backgroundColor: 'var(--theme-bg-modal, rgba(15, 23, 42, 0.96))',
          border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.15))',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.7)',
          color: 'var(--theme-text-primary, #f8fafc)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>📂</span>
            <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>
              Instantiate Workflow Blueprint
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--theme-text-muted, #94a3b8)', cursor: 'pointer', fontSize: '18px' }}
          >
            ✕
          </button>
        </div>

        {/* Templates Selection List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {templates.map((tpl) => {
            const isSelected = selectedTemplateId === tpl.id;
            return (
              <div
                key={tpl.id}
                onClick={() => setSelectedTemplateId(tpl.id)}
                style={{
                  padding: '14px',
                  backgroundColor: isSelected
                    ? 'var(--theme-accent-subtle, rgba(99, 102, 241, 0.15))'
                    : 'var(--theme-bg-card, rgba(15, 23, 42, 0.6))',
                  border: isSelected
                    ? '1px solid var(--theme-accent, #6366f1)'
                    : '1px solid var(--theme-border, rgba(255, 255, 255, 0.08))',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease, border-color 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--theme-text-primary, #fff)' }}>
                    {tpl.name}
                  </div>
                  <span
                    style={{
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      color: 'var(--theme-text-muted, #94a3b8)',
                      backgroundColor: 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.08))',
                      padding: '2px 6px',
                      borderRadius: '4px',
                    }}
                  >
                    {tpl.nodes.length} nodes
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--theme-text-secondary, #94a3b8)', marginTop: '4px', lineHeight: '1.4' }}>
                  {tpl.description}
                </div>
              </div>
            );
          })}
        </div>

        {/* Base Date Picker */}
        <div>
          <label style={{ fontSize: '11px', fontWeight: 700, color: 'var(--theme-text-secondary, #94a3b8)', textTransform: 'uppercase' }}>
            Base Start Date
          </label>
          <input
            type="date"
            value={baseDate}
            onChange={(e) => setBaseDate(e.target.value)}
            style={{
              width: '100%',
              marginTop: '4px',
              padding: '8px 10px',
              backgroundColor: 'var(--theme-bg-input, rgba(0, 0, 0, 0.3))',
              border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.1))',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '12px',
              outline: 'none',
            }}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 14px',
              background: 'none',
              border: 'none',
              color: 'var(--theme-text-muted, #94a3b8)',
              fontSize: '12px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleInstantiate}
            style={{
              padding: '8px 18px',
              backgroundColor: 'var(--theme-accent, #6366f1)',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Instantiate Blueprint
          </button>
        </div>
      </div>
    </div>
  );
};
